import { createFileRoute, redirect } from "@tanstack/react-router";

import { sdk } from "@/api/sdk";
import { PageLayout } from "@/components/layouts/page/layout";
import { connectSessionSocket, disconnectSessionSocket } from "@/features/auth/modules/session-socket/session-socket";
import { guardLegalConsents } from "@/features/legal/utils/legal-consent-gate";
import { alignAccountLocale } from "@/features/settings/utils/account-locale";

/** Who the profile already said is onboarded, so the check runs once per signed-in person. */
let onboardedUserId: string | null = null;

export const Route = createFileRoute("/(private)")({
  // Everything behind the login: no crawler can see it, so none should list it.
  staticData: { sitemap: false },
  component: PageLayout,
  // The socket follows the session: opened when the signed-in area is entered, closed when it
  // is left (sign-out, expired session). See features/auth/modules/session-socket/session-socket.ts.
  onEnter: connectSessionSocket,
  onLeave: disconnectSessionSocket,
  // Browser only, and every route under it inherits this. With SSR on, this
  // beforeLoad also ran on the server, where the session cookie is not on the
  // request, so every reload of a signed-in page bounced to /login and the
  // login page then said "You're signed in". Nothing behind the guard needs a
  // server render anyway.
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data } = await sdk.users.me.$get.send();

    if (!data) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }

    // The language saved on the account follows the person onto this device (and back).
    await alignAccountLocale(data.user);

    // Agreeing to the documents in force comes before anything else behind the login.
    await guardLegalConsents({ userId: data.user.id, location });

    // First sign-in: the one-time "About you" step. Checked until it is done, then never again.
    if (onboardedUserId !== data.user.id) {
      const profile = await sdk.athlete.profile.$get.send();
      if (profile.data && !profile.data.onboardedAt) {
        throw redirect({ to: "/onboarding", search: location.pathname === "/" ? {} : { redirect: location.href } });
      }
      if (profile.data?.onboardedAt) onboardedUserId = data.user.id;
    }

    // Past that one step, signed in is the whole bar. The starter also demanded an organization and sent everyone
    // else to onboarding, which is wrong here: a person who trains is a coach's client, not a
    // member of their workspace. Which coach they are looking at comes from their client record
    // (features/booking/hooks/use-booking-client.ts), and that record is created for them on
    // first visit, so there is no workspace to set up before the app is usable.
    return { user: data };
  },
});
